/**
 * Spatial slider transforms — make the three positional sliders on
 * `TeamTacticsConfig` actually move tokens on the pitch.
 *
 * `TeamTacticsConfig` has six sliders. Three are spatial (line height,
 * width, depth) — they describe team shape and SHOULD reposition the
 * back line, the wide players, and the front line respectively. The
 * other three (pressingIntensity, tempo, aggression) are behavioural —
 * they describe HOW the team plays, not WHERE the tokens sit. The
 * brief engine reads all six; this transform reads only the spatial
 * three.
 *
 * Slider semantics:
 *   - lineHeight (0..100, default 50)
 *       0 = on own goal line (extreme low block)
 *       50 = canonical formation y from the seed FORMATIONS table
 *       100 = back line pushed up ~halfway (high press)
 *       Affects: GK + back line (LB / CB / RB / LWB / RWB).
 *       Implementation: shifts z toward the attacking goal as the
 *       slider rises. Max shift is ±15 m at the extremes.
 *
 *   - width (0..100, default 50)
 *       0 = tucked narrow (wing-backs pinched into half-spaces)
 *       50 = canonical formation x
 *       100 = touchline-to-touchline (max stretch)
 *       Affects: wide players only (LB / RB / LWB / RWB / LM / RM /
 *       LW / RW). Multiplies |x| by 0.7..1.2 so a 50 slider preserves
 *       the formation exactly.
 *
 *   - depth (0..100, default 50)
 *       0 = compact (forwards pulled back toward midfield)
 *       50 = canonical
 *       100 = stretched (forwards pushed up another ~10 m)
 *       Affects: forwards (ST / CF / LW / RW / LF / RF).
 *
 * Pure, deterministic, side-aware: home attacks toward -z, away
 * toward +z. The transform applies in pitch-metres so it composes
 * cleanly with `projectFormation`'s output.
 */

import type { TacticsPlayerSpot } from './types';
import type { TeamTacticsConfig } from './teamTactics';

const LINE_HEIGHT_MAX_M = 15;
const DEPTH_MAX_M = 10;
// Width is piecewise so the 50 slider produces an exact identity
// transform — important so an untouched formation stays untouched and
// the no-op short-circuit kicks in.
const WIDTH_SCALE_NARROW = 0.7; // at slider 0 — tucked into half-spaces
const WIDTH_SCALE_WIDE = 1.2; // at slider 100 — touchline-to-touchline

const BACK_LINE_ROLES = new Set([
  'GK',
  'LB',
  'CB',
  'RB',
  'LWB',
  'RWB',
  'SW',
]);
const WIDE_ROLES = new Set([
  'LB',
  'RB',
  'LWB',
  'RWB',
  'LM',
  'RM',
  'LW',
  'RW',
  'LF',
  'RF',
]);
const FORWARD_ROLES = new Set(['ST', 'CF', 'LW', 'RW', 'LF', 'RF']);

function readSlider(
  tactics: TeamTacticsConfig | null | undefined,
  key: string,
): number {
  const v = tactics?.sliders?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 50;
}

export interface TeamShapeDeltas {
  /** Metres shifted toward the attacking goal at the current line-height. */
  lineHeightDeltaM: number;
  /** Metres added to forward-line z toward the attacking goal. */
  depthDeltaM: number;
  /** Multiplier applied to |x| for wide players. */
  widthScale: number;
}

/** Pure conversion from slider values to physical deltas. Exported for
 *  the rule-engine and HUD readouts that need to surface the same
 *  numbers as the board ("Line +6 m"). */
export function computeTeamShapeDeltas(
  tactics: TeamTacticsConfig | null | undefined,
  side: 'home' | 'away',
): TeamShapeDeltas {
  const lineHeight = readSlider(tactics, 'lineHeight');
  const width = readSlider(tactics, 'width');
  const depth = readSlider(tactics, 'depth');
  // Home attacks -z, so a HIGH line on the home team means z drops
  // toward 0. Away mirrors.
  const attackingSign = side === 'home' ? -1 : 1;
  // `(0) * sign` produces -0 when sign is -1; toBe(0) treats -0 ≠ +0
  // so we normalise to +0 explicitly. `+ 0` is the canonical idiom.
  const lineHeightDeltaM =
    ((lineHeight - 50) / 50) * LINE_HEIGHT_MAX_M * attackingSign + 0;
  const depthDeltaM = ((depth - 50) / 50) * DEPTH_MAX_M * attackingSign + 0;
  // Piecewise so width=50 → exactly 1.0 (no scaling). 0..50 narrows
  // toward WIDTH_SCALE_NARROW; 50..100 stretches toward WIDTH_SCALE_WIDE.
  const widthScale =
    width <= 50
      ? WIDTH_SCALE_NARROW + (width / 50) * (1 - WIDTH_SCALE_NARROW)
      : 1 + ((width - 50) / 50) * (WIDTH_SCALE_WIDE - 1);
  return { lineHeightDeltaM, depthDeltaM, widthScale };
}

/**
 * Apply the spatial sliders to a baseline formation. Returns a NEW
 * array with shifted spots; the input is untouched. When every spatial
 * slider sits at the default (50), the output equals the input
 * positionally — coaches keep their canonical formation until they
 * dial a knob.
 */
export function applyTeamShapeTransform(
  spots: TacticsPlayerSpot[],
  tactics: TeamTacticsConfig | null | undefined,
  side: 'home' | 'away',
): TacticsPlayerSpot[] {
  const { lineHeightDeltaM, depthDeltaM, widthScale } = computeTeamShapeDeltas(
    tactics,
    side,
  );
  // Identity short-circuit so we never allocate when nothing moves.
  if (lineHeightDeltaM === 0 && depthDeltaM === 0 && widthScale === 1) {
    return spots;
  }
  return spots.map((s) => {
    const role = s.role.toUpperCase();
    let x = s.x;
    let z = s.z;
    if (BACK_LINE_ROLES.has(role)) {
      z += lineHeightDeltaM;
    }
    // FORWARD vs WIDE may overlap (LW is both wide and forward). Each
    // axis is independent — forwards get z shifted, wide players get
    // x scaled, and the two compose for wingers.
    if (FORWARD_ROLES.has(role)) {
      z += depthDeltaM;
    }
    if (WIDE_ROLES.has(role)) {
      x *= widthScale;
    }
    return { ...s, x, z };
  });
}
